import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Breadcrumb from "../components/BreadCrumb";
import { FaSpinner } from "react-icons/fa";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Slideshow from "yet-another-react-lightbox/plugins/slideshow";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Video from "yet-another-react-lightbox/plugins/video";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import { 
  fetchYouTubePlaylists, 
  fetchPlaylistVideos, 
  fetchChannelVideos,
  getYouTubeEmbedUrl 
} from "../utils/youtubeUtils";

const YoutubeGallery = () => {
  const { playlistId } = useParams();
  const [playlists, setPlaylists] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPlaylist, setCurrentPlaylist] = useState(null);
  
  // YouTube video modal state
  const [currentVideo, setCurrentVideo] = useState(null);
  const videoModalRef = useRef(null);

  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const fetchData = async () => {
      setLoading(true);
      try {
        // First fetch all playlists
        const fetchedPlaylists = await fetchYouTubePlaylists();
        setPlaylists(fetchedPlaylists);
        
        if (playlistId) {
          // If a specific playlist is requested, fetch its videos
          const playlist = fetchedPlaylists.find(p => p.id === playlistId);
          if (playlist) {
            setCurrentPlaylist(playlist);
            const playlistVideos = await fetchPlaylistVideos(playlistId, 50); // Get up to 50 videos
            setVideos(playlistVideos);
          } else {
            setError(`Playlist with ID ${playlistId} not found`);
            // Try to fetch videos directly from channel
            const channelVideos = await fetchChannelVideos(30);
            setVideos(channelVideos);
          }
        } else {
          // If no specific playlist, fetch videos directly from the channel
          const channelVideos = await fetchChannelVideos(30);
          setVideos(channelVideos);
        }
      } catch (err) {
        console.error("Error fetching YouTube data:", err);
        setError("Failed to load YouTube data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [playlistId]);

  // Handle escape key for dialog
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape' && videoModalRef.current) {
        closeVideoModal();
      }
    };
    
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, []);

  const openVideoModal = (video) => {
    setCurrentVideo(video);
    if (videoModalRef.current) {
      videoModalRef.current.showModal();
    }
  };
  
  const closeVideoModal = () => {
    if (videoModalRef.current) {
      videoModalRef.current.close();
      // Clear the iframe src to stop the video
      const iframe = videoModalRef.current.querySelector('iframe');
      if (iframe) {
        const src = iframe.src;
        iframe.src = '';
        setTimeout(() => { iframe.src = src; }, 100);
      }
    }
  };

  const paths = [
    { label: t("homepage"), link: "/" },
    { label: t("portfolio"), link: "/portfolio" },
    { label: t("aruba_creative"), link: "/youtube-gallery" },
    ...(currentPlaylist ? [{ label: currentPlaylist.title, link: `/youtube-gallery/${currentPlaylist.id}` }] : []),
  ];

  if (loading) {
    return (
      <div className="w-full bg-white min-h-screen flex items-center justify-center pt-32">
        <FaSpinner className="animate-spin text-primary text-4xl" />
      </div>
    );
  }

  return (
    <div className="w-full bg-white min-h-screen">
      <div className="pt-32">
        <Breadcrumb paths={paths} />
      </div>
      <div className="container mx-auto px-3 lg:px-0 pb-32">
        <div className="pt-10 pb-10">
          <div className="flex flex-col leading-relaxed tracking-normal">
            <h1 className="font-northuraBlack text-4xl md:text-6xl lg:text-7xl">
              {currentPlaylist ? currentPlaylist.title : t("aruba_creative")}
            </h1>
            <p className="text-3xl md:text-5xl lg:text-6xl font-northuraBold">
              {t("youtube_showcase")}
            </p>
          </div>
        </div>

        {/* Playlist Navigation if not showing a specific playlist */}
        {!playlistId && playlists.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl mb-4">{t("playlists")}</h2>
            <div className="flex flex-wrap gap-4">
              {playlists.map((playlist) => (
                <a 
                  key={playlist.id}
                  href={`/youtube-gallery/${playlist.id}`}
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/80 transition-colors"
                >
                  {playlist.title}
                </a>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className="py-6 text-center">
            <p className="text-red-500">{error}</p>
          </div>
        )}

        {/* Videos Grid */}
        {videos.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {videos.map((video, idx) => (
              <div 
                key={idx} 
                onClick={() => openVideoModal(video)} 
                className="cursor-pointer rounded-lg overflow-hidden"
              >
                <div className="w-full h-48 relative bg-black rounded-lg overflow-hidden group">
                  <img
                    src={video.thumbnailUrl}
                    alt={video.title}
                    className="w-full h-full object-cover transition-opacity duration-500 ease-in-out opacity-80 group-hover:opacity-100"
                    onError={(e) => {
                      e.target.src = "/images/placeholder.jpg";
                    }}
                    loading="lazy"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 bg-primary/80 rounded-full flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="mt-3">
                  <h3 className="text-lg font-semibold line-clamp-2">{video.title}</h3>
                  <p className="text-gray-500 text-sm mt-1">
                    {new Date(video.publishedAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : !loading && !error && (
          <div className="py-20 text-center">
            <p className="text-gray-500">No videos found.</p>
          </div>
        )}
      </div>

      {/* Native HTML dialog for YouTube videos */}
      <dialog
        ref={videoModalRef}
        onClick={(e) => {
          // Close if clicking outside the iframe
          if (e.target === videoModalRef.current) {
            closeVideoModal();
          }
        }}
        style={{
          padding: 0,
          border: 'none',
          backgroundColor: 'transparent',
          maxWidth: '95vw',
          maxHeight: '90vh',
          width: '800px',
          overflow: 'visible',
          borderRadius: '8px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.9)'
        }}
      >
        {currentVideo && (
          <>
            <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%' }}>
              <iframe
                src={`https://www.youtube.com/embed/${currentVideo.id}?autoplay=1&mute=1&rel=0&modestbranding=1`}
                title={currentVideo.title}
                style={{ 
                  position: 'absolute', 
                  top: 0, 
                  left: 0, 
                  width: '100%', 
                  height: '100%',
                  border: 'none',
                  borderRadius: '8px'
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <button
              onClick={closeVideoModal}
              style={{
                position: 'absolute',
                top: '-40px',
                right: '-10px',
                backgroundColor: 'white',
                color: 'black',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
                zIndex: 100
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </>
        )}
      </dialog>
    </div>
  );
};

export default YoutubeGallery; 